import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { UserModel } from 'src/app/models/authentication/user.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { OptionWindowUserTableModel } from 'src/app/models/tables/authentication/option-window-user-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { OptionWindowService } from './../../../../services/authentication/option-window.service';

@Component({
  selector: 'app-permissions-user',
  templateUrl: './permissions-user.component.html',
  styleUrls: ['./permissions-user.component.scss'],
  standalone: false
})
export class PermissionsUserComponent implements OnInit, OnDestroy {
  ascendant: boolean = true;
  columnSort: string | null = null;
  companyId: number = 0;
  data: PagedResponseModel<OptionWindowUserTableModel> = new PagedResponseModel<OptionWindowUserTableModel>();
  disabledButton: boolean = false;
  filters: ColumnResponseModel[] = [];
  header: ColumnResponseModel[] = [];
  limit: number = 10;
  output$: Subject<any>;
  page: number = 1;
  subscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Modificar',
      icon: 'pi pi-sync',
      action: 'edit'
    },
  ];
  userContract: UserModel = new UserModel();

  constructor(public config: DynamicDialogConfig,
    private confirmDialogService: ConfirmDialogService,
    private toastHandlerService: ToastHandlerService,
    private optionWindowService: OptionWindowService,) {
  }

  async ngOnInit(): Promise<void> {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.userContract = inputs.userModel;
    });
    await Promise.all([
      this.getColumns(),
      this.getAllOptionWindowUsersWithPagination()
    ]);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }


  async getAllOptionWindowUsersWithPagination() {
    try {
      this.data = await firstValueFrom(this.optionWindowService.GetOptionsWindowsByUserWithPagination(this.userContract.userId, this.page,
        this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al obtener los permisos por usuarios.', error);
    }
  }

  async getColumns() {
    try {
      this.header = await firstValueFrom(this.optionWindowService.getColumns());
    } catch (error) {
      console.error('Error al obtener las columnas de permisos por usuarios.', error);
    }
  }

  async onAction(event: { action: string, item?: OptionWindowUserTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      if (await this.confirmDialogService.confirm('¿Realmente desea modificar los permisos por ventana para este usuario?')) {
        await this.savePermissions(event.item.optionWindowId);
      }
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllOptionWindowUsersWithPagination();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async saveOptionWindowUserPermission(userId: number, optionWindowId: number) {
    try {
      await firstValueFrom(this.optionWindowService.saveOptionWindowsByUser(userId, optionWindowId));
    } catch (error) {
      console.error('Error al guardar el permiso por ventana para el usuario.', error);
    }
  }

  async savePermissions(optionWindowId: number) {
    try {
      await this.saveOptionWindowUserPermission(this.userContract.userId, optionWindowId);
      await this.getAllOptionWindowUsersWithPagination();
      this.toastHandlerService.showToast(
        'success',
        'El permiso por usuario se actualizo correctamente.'
      );
    } catch (error) {
      console.error('Error al guardar el permiso para el usuario.', error);
    }
  }

  async assignAllPermissions() {
    try {
      if (await this.confirmDialogService.confirm('¿Realmente desea asignar todos los permisos a este usuario?')) {
        const result = await firstValueFrom(this.optionWindowService.activateAllPermissionsByUser(this.userContract.userId));
        if (result) {
          await this.getAllOptionWindowUsersWithPagination();
          this.toastHandlerService.showToast(
            'success',
            'Se asignaron todos los permisos correctamente.'
          );
        } else {
          this.toastHandlerService.showToast(
            'error',
            'Error al asignar todos los permisos.'
          );
        }
      }
    } catch (error) {
      console.error('Error al asignar todos los permisos.', error);
    }
  }

  async removeAllPermissions() {
    try {
      const accepted = await this.confirmDialogService.confirm('¿Realmente desea quitar todos los permisos a este usuario?');
      if (accepted) {
        const result = await firstValueFrom(this.optionWindowService.deactivateAllPermissionsByUser(this.userContract.userId));
        if (result) {
          await this.getAllOptionWindowUsersWithPagination();
          this.toastHandlerService.showToast(
            'success',
            'Se quitaron todos los permisos correctamente.'
          );
        } else {
          this.toastHandlerService.showToast(
            'error',
            'Error al quitar todos los permisos.'
          );
        }
      }
    } catch (error) {
      console.error('Error al quitar todos los permisos.', error);
    }
  }
}